import { Link } from "react-router-dom";
import {
  ArrowRight,
  TrendingDown,
  Calendar,
  CheckCircle2,
  ShieldCheck,
  FileText,
  Phone,
  AlertTriangle,
  Scale,
} from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { HeroBackground } from "@/components/site/HeroBackground";
import { stockImages } from "@/data/site/stockImages";
import { PageSEO } from "@/components/site/PageSEO";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { CTASection } from "@/components/site/CTASection";
import { caseResults, resultsDisclaimer } from "@/data/site/results";
import { brand } from "@/data/site/brand";
import { Reveal, RevealStagger, RevealItem } from "@/components/motion/Reveal";

const aggregateStats = [
  { label: "Total liability handled", value: "$649K+", icon: Scale, detail: "Combined balances across cases shown below" },
  { label: "Total client savings", value: "$291K+", icon: TrendingDown, detail: "Reductions, abatements, and OIC acceptances" },
  { label: "Fastest levy release", value: "48 hrs", icon: AlertTriangle, detail: "POA filed same day, levy lifted in 2 business days" },
  { label: "OIC acceptance rate", value: "First review", icon: CheckCircle2, detail: "Most CTR OICs accepted on first IRS pass" },
];

const methodology = [
  {
    title: "Every number is real",
    detail:
      "Balances owed, IRS-accepted settlements, and timeframes come directly from closed engagement files. We never round up or aggregate across multiple cases.",
  },
  {
    title: "Identifying details removed",
    detail:
      "Names, exact dates, locations, and business identifiers are removed to protect client confidentiality under IRS §7216 and our professional ethics rules.",
  },
  {
    title: "Procedure, not promises",
    detail:
      "Your case will have different facts and a different outcome. What stays the same is the procedure — Form 2848, transcripts, Form 433-A/B, and the IRS criteria we work to.",
  },
];

const Results = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "CTR Tax Resolution Case Results",
    itemListElement: caseResults.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.service,
      description: c.story,
      url: `${brand.siteUrl}${c.href}`,
    })),
  };

  return (
    <SiteLayout>
      <PageSEO
        title="Case Results"
        description="Anonymized examples of IRS tax resolution outcomes from Comprehensive Tax Resolution LLC — Offers in Compromise, levy releases, payroll tax resolutions, audit defense, and more."
        path="/results"
        image={stockImages.financialChart.src}
        jsonLd={jsonLd}
      />

      <section className="relative bg-gradient-navy text-white overflow-hidden">
        <HeroBackground image={stockImages.financialChart} priority />
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-12 lg:py-28 max-w-6xl">
          <Reveal>
            <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "Case Results" }]} tone="dark" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mt-2 mb-4">
              What outcomes look like
            </p>
            <h1 className="font-heading text-4xl lg:text-6xl font-bold leading-[1.05] mb-6 [text-wrap:balance]">
              Real cases.<br />
              <span className="text-gold">Real numbers.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-2xl leading-relaxed">
              Identifying details removed; balances, settlements, and timelines are accurate.
              Your case is different — but the procedures and the math are the same.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Aggregate stats */}
      <section className="py-12 lg:py-14 border-b border-border bg-secondary/30">
        <div className="container mx-auto px-4 max-w-6xl">
          <RevealStagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {aggregateStats.map((s) => (
              <RevealItem key={s.label}>
                <div className="bg-white p-6 lg:p-7 h-full">
                  <s.icon className="w-6 h-6 text-gold mb-3" strokeWidth={1.75} />
                  <p className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-none mb-2">
                    {s.value}
                  </p>
                  <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-navy mb-2">
                    {s.label}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{s.detail}</p>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Case results — magazine cards */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <Reveal>
            <div className="mb-12 max-w-3xl">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
                Selected case results
              </p>
              <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-tight">
                Six representative cases — and what the IRS actually accepted.
              </h2>
            </div>
          </Reveal>

          <RevealStagger className="space-y-6" stagger={60}>
            {caseResults.map((c, idx) => (
              <RevealItem key={c.id}>
                <article className="rounded-lg border border-border bg-white overflow-hidden grid lg:grid-cols-[1fr_320px]">
                  <div className="p-7 lg:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="font-heading text-xs font-bold text-muted-foreground">
                        Case {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
                        {c.service}
                      </span>
                    </div>
                    <p className="text-foreground/85 leading-[1.75] text-[17px] mb-7">{c.story}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="w-4 h-4 text-gold" />
                      <span>Timeline: <strong className="text-navy font-semibold">{c.timeframe}</strong></span>
                      <span className="mx-2">·</span>
                      <Link
                        to={c.href}
                        className="font-semibold text-navy hover:text-gold inline-flex items-center gap-1"
                      >
                        About this service <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                  <div className="bg-gradient-navy text-white p-6 lg:p-7 grid grid-cols-3 lg:grid-cols-1 lg:auto-rows-fr gap-0 lg:divide-y lg:divide-white/10 divide-x lg:divide-x-0 divide-white/10">
                    <Stat label="Owed before" value={c.owed} />
                    <Stat label="Resolved for" value={c.settled} />
                    <Stat label="Saved" value={c.saved} highlight />
                  </div>
                </article>

              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-16 lg:py-20 bg-secondary/30 border-y border-border">
        <div className="container mx-auto px-4 max-w-6xl">
          <Reveal>
            <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-start">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-gold mb-3">
                  Methodology
                </p>
                <h2 className="font-heading text-3xl lg:text-4xl font-bold text-navy leading-tight mb-4">
                  How to read these numbers.
                </h2>
                <p className="text-foreground/75 leading-relaxed">
                  Anonymized doesn't mean estimated. We document every case the way we'd want
                  the IRS to see it: facts, dollars, and dates.
                </p>
              </div>
              <RevealStagger className="space-y-5">
                {methodology.map((m) => (
                  <RevealItem key={m.title}>
                    <div className="flex gap-4 bg-white rounded-lg border border-border p-5">
                      <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                      <div>
                        <p className="font-heading font-bold text-navy mb-1">{m.title}</p>
                        <p className="text-sm text-foreground/75 leading-relaxed">{m.detail}</p>
                      </div>
                    </div>
                  </RevealItem>
                ))}
              </RevealStagger>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What these cases have in common */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <Reveal>
            <ShieldCheck className="w-10 h-10 text-gold mx-auto mb-5" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4 leading-tight max-w-3xl mx-auto">
              The pattern across every result above? Acting before the next IRS deadline.
            </h2>
            <p className="text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-8">
              The taxpayers above ranged from $28K in penalties to $240K in payroll-tax exposure.
              The factor that shifted their outcome wasn't size of debt — it was filing
              representation before the IRS escalated to seizure, lien, or levy.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-7 py-4 rounded text-lg transition-transform hover:scale-[1.03]"
              >
                <Phone className="w-5 h-5" /> Call {brand.phone}
              </a>
              <Link
                to="/free-consultation"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-secondary text-navy font-semibold px-7 py-4 rounded text-lg border border-border"
              >
                <FileText className="w-5 h-5" /> Free case evaluation
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-10 bg-secondary/40 border-t border-border">
        <div className="container mx-auto px-4 max-w-5xl">
          <p className="text-xs text-muted-foreground italic leading-relaxed">{resultsDisclaimer}</p>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

const Stat = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div className="flex flex-col justify-center px-3 lg:px-1 py-2 lg:py-3 min-w-0">
    <dt className="text-[10px] font-bold uppercase tracking-[0.15em] text-white/60 mb-1.5 truncate">
      {label}
    </dt>
    <dd
      className={`font-heading font-bold text-lg lg:text-2xl leading-tight tabular-nums truncate ${
        highlight ? "text-gold" : "text-white"
      }`}
    >
      {value}
    </dd>
  </div>
);


export default Results;
